import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { peticion } from '../api/cliente.js';
import type { Entidad } from '../api/tipos.js';
import { Aviso, Cargando, EstadoVacio } from '../componentes/comunes.js';
import { textos } from '../textos.js';

/**
 * Paso 1: elegir entidad.
 *
 * Va **antes** del acceso porque determina contra qué maestro se comprueban los
 * datos (§3, §9). No exige sesión.
 */
export function PantallaEntidad({ alElegir }: { alElegir: (entidad: Entidad) => void }) {
  const [busqueda, setBusqueda] = useState('');
  const consulta = useQuery({
    queryKey: ['entidades'],
    queryFn: () => peticion<{ entidades: Entidad[] }>('/entidades'),
  });
  const entidades = consulta.data?.entidades ?? [];
  const termino = normalizar(busqueda);
  const filtradas = entidades.filter((entidad) =>
    normalizar(`${entidad.nombre} ${entidad.codigo}`).includes(termino),
  );

  return (
    <section className="pantalla-entidad" aria-labelledby="titulo-entidad">
      <h2 id="titulo-entidad">{textos.entidad.titulo}</h2>
      <p className="ayuda">{textos.entidad.ayuda}</p>

      {consulta.isPending && <Cargando />}
      {consulta.isError && (
        <Aviso tipo="error">
          {textos.errores.generico}{' '}
          <button type="button" className="enlace" onClick={() => void consulta.refetch()}>
            {textos.aplicacion.reintentar}
          </button>
        </Aviso>
      )}

      {entidades.length > 4 && (
        <label className="buscador">
          <span>{textos.entidad.buscar}</span>
          <span className="control-buscador">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m16 16 4 4" />
            </svg>
            <input
              type="search"
              value={busqueda}
              placeholder={textos.entidad.buscarPlaceholder}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />
          </span>
        </label>
      )}

      {consulta.data !== undefined && entidades.length === 0 && (
        <EstadoVacio titulo={textos.entidad.ninguna} detalle={textos.entidad.ningunaAyuda} />
      )}

      {entidades.length > 0 && filtradas.length === 0 && (
        <EstadoVacio
          titulo={textos.entidad.sinResultados}
          detalle={textos.entidad.sinResultadosAyuda}
          compacto
        />
      )}

      {filtradas.length > 0 && (
        <ul className="opciones">
          {filtradas.map((entidad) => (
            <li key={entidad.codigo}>
              <button type="button" className="opcion" onClick={() => alElegir(entidad)}>
                <span
                  className="icono-opcion"
                  style={{ color: entidad.colorHex ?? 'var(--acento)' }}
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M4 20V7l8-4 8 4v13M8 10h2m4 0h2m-8 4h2m4 0h2m-6 6v-3h4v3" />
                  </svg>
                </span>
                <span className="texto-opcion">
                  <span className="nombre">{conPrefijoTs(entidad.nombre)}</span>
                  <span className="detalle">{entidad.codigo}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Antepone «TS» al nombre de la marca, salvo que ya empiece por ahí (p. ej. «TS Facturas»). */
function conPrefijoTs(nombre: string): string {
  return nombre.startsWith('TS ') ? nombre : `TS ${nombre}`;
}

function normalizar(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es');
}
